/**
 * Memberstack DOM SDK integration for the loom.
 *
 * useMemberAccess() returns one of:
 *   { status: "loading" }
 *   { status: "anonymous" }   — no Memberstack session visible
 *   { status: "unauthorised" } — signed in, but plan doesn't match qualifying IDs
 *   { status: "member" }       — signed in AND on a qualifying 1% Club plan
 *
 * Plus a `refresh()` to re-check the session on demand (useful after a
 * member returns from join/login on the awake subdomain).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import memberstackDOM from "@memberstack/dom";
import { getActiveWmToken, getStoredWmToken, clearWmToken } from "./wmToken";

const QUALIFYING_PRICE_IDS = new Set([
  "prc_1-club-monthly-txnv03iu",
  "prc_1-annual-n2170ed3",
  // Free comp tier for The Weave's team / co-founders. Has no price id
  // (free plan), so this is matched via the plan-id fallback in
  // hasQualifyingPlan() below.
  "pln_weave-team-np3a09sc",
]);

const PUBLIC_KEY = process.env.REACT_APP_MEMBERSTACK_PUBLIC_KEY;
export const JOIN_URL =
  process.env.REACT_APP_JOIN_URL || "https://awake.wearetheweave.co.uk/join";
export const LOGIN_URL =
  process.env.REACT_APP_LOGIN_URL || "https://awake.wearetheweave.co.uk/login";

let memberstackInstance = null;

export function getMemberstack() {
  if (memberstackInstance) return memberstackInstance;
  if (!PUBLIC_KEY) return null;
  try {
    memberstackInstance = memberstackDOM.init({
      publicKey: PUBLIC_KEY,
      domain: "https://memberstack-client.wearetheweave.co.uk",
      useCookies: true,
    });
  } catch {
    memberstackInstance = null;
  }
  return memberstackInstance;
}

function extractMember(memberData) {
  if (!memberData) return null;
  return {
    id: memberData.id,
    email: memberData.auth?.email || memberData.email || null,
    firstName:
      memberData.customFields?.["first-name"] ||
      memberData.customFields?.firstName ||
      null,
  };
}

function describePlanConnections(memberData) {
  if (!memberData) return [];
  const connections = memberData.planConnections || memberData.plans || [];
  return connections.map((c) => ({
    active:
      c.active === true ||
      c.status === "ACTIVE" ||
      c.status === "TRIALING" ||
      Boolean(c.payment?.priceId),
    status: c.status,
    planId: c.planId,
    priceId: c.priceId || c.payment?.priceId || null,
  }));
}

function hasQualifyingPlan(memberData) {
  if (!memberData) return false;
  const connections =
    memberData.planConnections || memberData.plans || [];
  return connections.some((c) => {
    if (!c) return false;
    const active =
      c.active === true ||
      c.status === "ACTIVE" ||
      c.status === "TRIALING" ||
      Boolean(c.payment?.priceId);
    if (!active) return false;
    const candidates = [
      c.priceId,
      c.payment?.priceId,
      c.planId,
    ].filter(Boolean);
    return candidates.some((id) => QUALIFYING_PRICE_IDS.has(id));
  });
}

export function useMemberAccess() {
  // First-paint state: if a valid `?wm=` token is in the URL (or carried in
  // sessionStorage from earlier this tab), we grant member access
  // synchronously — no paywall flicker while Memberstack's async check runs.
  const [state, setState] = useState(() => {
    const token = getActiveWmToken();
    if (token) {
      return {
        status: "member",
        member: { id: null, email: token.e || null, firstName: null },
        debug: { viaWmToken: true, tokenPayload: token },
      };
    }
    return { status: "loading", member: null, debug: null };
  });
  const cancelledRef = useRef(false);

  const check = useCallback(async () => {
    const ms = getMemberstack();
    const tokenPayload = getStoredWmToken(); // re-read each check; may have expired

    if (!ms) {
      if (!cancelledRef.current) {
        if (tokenPayload) {
          setState({
            status: "member",
            member: { id: null, email: tokenPayload.e || null, firstName: null },
            debug: { viaWmToken: true, tokenPayload, reason: "no-sdk" },
          });
        } else {
          setState({ status: "anonymous", member: null, debug: { reason: "no-sdk" } });
        }
      }
      return;
    }
    try {
      const result = await ms.getCurrentMember();
      if (cancelledRef.current) return;
      const memberData = result?.data ?? null;
      const member = extractMember(memberData);
      const planConnections = describePlanConnections(memberData);
      const qualifying = hasQualifyingPlan(memberData);

      const debug = {
        hasSession: Boolean(memberData),
        memberId: memberData?.id || null,
        email: member?.email || (tokenPayload?.e ?? null),
        planConnections,
        qualifyingPlan: qualifying,
        qualifyingPriceIds: Array.from(QUALIFYING_PRICE_IDS),
        viaWmToken: Boolean(tokenPayload) && !memberData,
        tokenPayload: tokenPayload || null,
      };

      // eslint-disable-next-line no-console
      console.info("[Pull the Thread] Memberstack check:", debug);

      let status;
      let resolvedMember = member;
      if (memberData) {
        // Memberstack is the source of truth when it sees a session.
        status = qualifying ? "member" : "unauthorised";
      } else if (tokenPayload) {
        // No Memberstack session but the friendly-fence token is valid.
        status = "member";
        resolvedMember = { id: null, email: tokenPayload.e || null, firstName: null };
      } else {
        status = "anonymous";
        resolvedMember = null;
      }

      setState({ status, member: resolvedMember, debug });
    } catch (err) {
      if (cancelledRef.current) return;
      // eslint-disable-next-line no-console
      console.warn("[Pull the Thread] Memberstack getCurrentMember failed:", err);
      if (tokenPayload) {
        setState({
          status: "member",
          member: { id: null, email: tokenPayload.e || null, firstName: null },
          debug: { viaWmToken: true, tokenPayload, reason: "ms-error", message: String(err) },
        });
      } else {
        setState({
          status: "anonymous",
          member: null,
          debug: { reason: "error", message: String(err) },
        });
      }
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    check();

    let unsubscribe = null;
    const ms = getMemberstack();
    if (ms) {
      try {
        const sub = ms.onAuthChange?.(() => check());
        unsubscribe = sub?.unsubscribe ? () => sub.unsubscribe() : null;
      } catch {
        /* SDK without onAuthChange — initial fetch is enough */
      }
    }

    // Also re-check when the tab regains focus (e.g. user just came back
    // from signing in on awake.wearetheweave.co.uk).
    function onFocus() {
      check();
    }
    function onVisibility() {
      if (document.visibilityState === "visible") check();
    }
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelledRef.current = true;
      if (unsubscribe) unsubscribe();
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [check]);

  return { ...state, refresh: check };
}

export async function memberstackLogout() {
  // Clear our friendly-fence token too — otherwise a logged-out user would
  // remain "member" for the rest of the tab session via the stored payload.
  clearWmToken();
  const ms = getMemberstack();
  if (!ms) return;
  try {
    await ms.logout();
  } catch {
    /* swallow — we'll hard-redirect anyway */
  }
}
